/**
 * L'état du monde et ses transitions — **sans DOM, sans Three.js, sans horloge**.
 *
 * Ce module était le cœur de `main.ts`, et y était intestable par construction :
 * importer `main.ts` écrit dans `#app`, monte un contexte WebGL et démarre une
 * boucle `requestAnimationFrame`. Il n'y avait rien à en extraire sans tout
 * déclencher, si bien que dix transitions applicatives — celles qui décident ce
 * qu'il advient des sillages quand on supprime un corps, ou de la caméra quand
 * on change de scénario — n'étaient vérifiées nulle part.
 *
 * Rien de ce qui suit n'est nouveau : c'est le `dispatch` d'avant, déplacé et
 * rendu pur. `main.ts` garde le montage, les écouteurs et le rendu.
 *
 * ## La forme
 *
 * `reduce(state, action)` rend un **nouvel** état et la liste de ce qu'il faut
 * redessiner. Il ne touche à rien : ni au DOM, ni à l'historique des sillages,
 * qu'il se contente de *demander* par un effet. C'est ce qui permet de vérifier
 * qu'un scénario chargé efface bien les sillages du précédent sans avoir à
 * monter quoi que ce soit.
 *
 * ## Ce qui n'est délibérément pas changé
 *
 * `stepsPerFrame` reste un `number` nu, comme avant. CLAUDE.md §1 vise les
 * signatures exportées du **noyau** ; ce module n'en fait pas partie, et
 * transformer au passage un compte d'images en `Quantity` mêlerait un
 * changement de contrat à une extraction qui doit rester mécaniquement
 * équivalente.
 */

import type { World } from "../core/world.js";
import type { TrailHistory } from "../world/trails.js";
import { SCENARIOS, scenarioById } from "../world/scenarios.js";
import { placeBody, moveBody, setVelocity, type BodyPlacement, addPlanet, removeEntity, setMass } from "../world/actions.js";
import { advanceWorld } from "../core/world.js";
import { dimensionless, quantity, valueIn } from "../core/units.js";

/**
 * Le pas d'intégration : une heure, toujours.
 *
 * La vitesse agit sur le **nombre** de pas exécutés par image, jamais sur leur
 * taille — c'est ce qui rend la trajectoire indépendante de la machine
 * (CLAUDE.md §5).
 */
export const STEP = quantity(3600, "s");

/** Pas par image au démarrage. */
export const INITIAL_STEPS_PER_FRAME = 40;

export interface AppState {
  readonly scenarioId: string;
  readonly world: World;
  /** Le monde de référence auquel la dérive d'énergie est rapportée. */
  readonly origin: World;
  readonly selectedId: string;
  /** Corps auquel la caméra est accrochée : il devient l'origine du repère. */
  readonly focusId: string;
  readonly running: boolean;
  readonly stepsPerFrame: number;
}

export type AppAction =
  | { readonly type: "place-body"; readonly kind: "planet" | "star"; readonly at: BodyPlacement }
  | { readonly type: "move-body"; readonly x: number; readonly y: number }
  | { readonly type: "set-velocity"; readonly vx: number; readonly vy: number }
  | { readonly type: "advance" }
  | { readonly type: "select"; readonly id: string; readonly travel: boolean }
  | { readonly type: "focus"; readonly id: string }
  | { readonly type: "load-scenario"; readonly id: string }
  | { readonly type: "add-planet" }
  | { readonly type: "remove-selected" }
  | { readonly type: "toggle-follow" }
  | { readonly type: "set-mass"; readonly kilograms: number }
  | { readonly type: "toggle-running" }
  | { readonly type: "set-speed"; readonly stepsPerFrame: number };

/**
 * Ce que la transition demande à l'historique des sillages.
 *
 * Le réducteur ne l'exécute pas : un historique est une mémoire mutable, et la
 * garder hors du réducteur est ce qui le laisse pur. Le dire en effet plutôt
 * que de le faire rend aussi la règle vérifiable — « retirer un corps oublie
 * son sillage » devient une assertion, au lieu d'un appel qu'il faudrait
 * espionner.
 */
export type TrailEffect =
  | { readonly kind: "record" }
  | { readonly kind: "clear" }
  | { readonly kind: "retain"; readonly keep: ReadonlySet<string> };

export interface RenderEffects {
  readonly scenarioBar?: boolean;
  readonly entities?: boolean;
  readonly inspector?: "full" | "facts";
  readonly hud?: boolean;
  readonly redraw?: boolean;
  readonly reframe?: boolean;
  readonly lookAt?: string;
  readonly trails?: TrailEffect;
}

export interface Reduced {
  readonly state: AppState;
  readonly effects: RenderEffects;
}

/**
 * L'état de départ : le premier scénario, son premier corps sélectionné, en
 * marche.
 */
export function initialState(): AppState {
  const first = SCENARIOS[0];
  if (first === undefined) throw new Error("aucun scénario");

  const world = first.seed();
  return {
    scenarioId: first.id,
    world,
    origin: world,
    selectedId: world.entities[0]?.id ?? "",
    focusId: "",
    running: true,
    stepsPerFrame: INITIAL_STEPS_PER_FRAME,
  };
}

/**
 * Exécute sur l'historique des sillages ce que la transition a demandé.
 *
 * Séparée de `reduce` parce qu'un historique est une mémoire mutable, et
 * exportée parce qu'une intention déclarée que personne n'exécute ne vaut rien.
 * C'est le défaut que la séparation aurait pu introduire : le réducteur annonce
 * proprement « efface les sillages », et plus personne ne les efface. Ni erreur,
 * ni test rouge — seulement des fantômes à l'écran.
 *
 * `world` est celui de l'état **d'après** : enregistrer un point, c'est
 * enregistrer où les corps viennent d'arriver.
 */
export function applyTrails(
  history: TrailHistory,
  world: World,
  effects: RenderEffects,
): void {
  const wanted = effects.trails;
  if (wanted === undefined) return;

  if (wanted.kind === "clear") {
    history.clear();
    return;
  }
  if (wanted.kind === "retain") {
    history.retain(wanted.keep);
    return;
  }
  for (const one of world.entities) {
    history.record(one.id, {
      x: valueIn(one.position.x, "m"),
      y: valueIn(one.position.y, "m"),
    });
  }
}

/** Toutes les mutations applicatives et leurs conséquences visibles vivent ici. */
function transition(state: AppState, action: AppAction): Reduced {
  switch (action.type) {
    case "advance": {
      return {
        state: {
          ...state,
          world: advanceWorld(state.world, STEP, dimensionless(state.stepsPerFrame)),
        },
        effects: { trails: { kind: "record" } },
      };
    }

    case "select": {
      const moved = action.id !== state.selectedId;
      const next = moved ? { ...state, selectedId: action.id } : state;
      const seen: RenderEffects = moved ? { entities: true, inspector: "full" } : {};
      return {
        state: next,
        effects: action.travel ? { ...seen, lookAt: action.id } : seen,
      };
    }

    case "focus": {
      if (action.id === state.focusId) return { state, effects: {} };
      return { state: { ...state, focusId: action.id }, effects: { inspector: "full" } };
    }

    case "load-scenario": {
      const scenario = scenarioById(action.id);
      if (scenario === undefined) return { state, effects: {} };

      const world = scenario.seed();
      return {
        state: {
          ...state,
          scenarioId: action.id,
          world,
          origin: world,
          selectedId: world.entities[0]?.id ?? "",
          focusId: "",
        },
        effects: {
          scenarioBar: true,
          entities: true,
          inspector: "full",
          hud: true,
          reframe: true,
          trails: { kind: "clear" },
        },
      };
    }

    case "add-planet": {
      const world = addPlanet(state.world);
      return {
        state: {
          ...state,
          world,
          origin: world,
          selectedId: world.entities[world.entities.length - 1]?.id ?? state.selectedId,
        },
        effects: { entities: true, inspector: "full" },
      };
    }

    case "remove-selected": {
      const removed = state.selectedId;
      const world = removeEntity(state.world, removed);
      return {
        state: {
          ...state,
          world,
          origin: world,
          selectedId: world.entities[0]?.id ?? "",
          focusId: state.focusId === removed ? "" : state.focusId,
        },
        effects: {
          entities: true,
          inspector: "full",
          trails: { kind: "retain", keep: new Set(world.entities.map((one) => one.id)) },
        },
      };
    }

    case "toggle-follow": {
      return {
        state: {
          ...state,
          focusId: state.focusId === state.selectedId ? "" : state.selectedId,
        },
        effects: { inspector: "full" },
      };
    }

    case "place-body": {
      const world = placeBody(state.world, action.kind, action.at);
      return { state: { ...state, world, origin: world, selectedId: world.entities.at(-1)?.id ?? "" },
        effects: { entities: true, inspector: "full", redraw: true } };
    }
    case "move-body": {
      const world = moveBody(state.world, state.selectedId, action.x, action.y);
      return { state: { ...state, world, origin: world },
        effects: { inspector: "full", redraw: true, trails: { kind: "clear" } } };
    }
    case "set-velocity": {
      const world = setVelocity(state.world, state.selectedId, action.vx, action.vy);
      return { state: { ...state, world, origin: world },
        effects: { inspector: "full", redraw: true } };
    }
    case "set-mass": {
      const world = setMass(state.world, state.selectedId, action.kilograms);
      return {
        state: { ...state, world, origin: world },
        effects: { inspector: "facts", redraw: true },
      };
    }

    case "toggle-running": {
      return { state: { ...state, running: !state.running }, effects: { hud: true } };
    }

    case "set-speed": {
      return { state: { ...state, stepsPerFrame: action.stepsPerFrame }, effects: {} };
    }
  }
}

/** Les dépendances du dessin sont dérivées des changements, pas recopiées par action. */
export function reduce(state: AppState, action: AppAction): Reduced {
  const result = transition(state, action);
  if (action.type === "advance") return result;
  const next = result.state;
  return {
    state: next,
    effects: {
      ...result.effects,
      ...(next.world !== state.world || next.selectedId !== state.selectedId || next.focusId !== state.focusId
        ? { redraw: true } : {}),
      ...(next.origin !== state.origin ? { hud: true } : {}),
    },
  };
}
